import { store } from "../store";
import DataStructure, { sanitize } from "./DataStructure";
import DocumentRef from "./DocumentRef";
import COLORS from "../colors";
import { deepFreeze, formatCurrency } from "../util/misc";
import { cleanVendorQuotes } from "../util/vendorOrderNumber";
import { every, get, uniq } from "lodash";

// A type must declare every field an outcome of that type can hold, which is
// more than its own form edits. `sanitize` picks by the type's own keys, so an
// undeclared field is dropped the next time the outcome is saved — including
// fields written by something other than the lead editor, and fields that only
// belong to a later type but have to survive a pass through this one.
export const SALES_LEAD_OUTCOME_FIELDS = {
  pending: {},
  // The customer supplies `dateRequired` when signing the quote, so it lands
  // here (via `quoteResponse` in functions/quotes.js) before the sale exists
  // and long before the "sold" form asks for it.
  "pending-sale": {
    aia: false,
    notaxCert: null,
    intentLetter: null,
    quote: null,
    dateRequired: null,
    contracts: [],
    downPayment: null,
    deferDownPayment: false,
    deferOrdering: false,
    vendorQuotes: [],
    expectCustomerContract: false,
  },
  // A change order parks an already-sold lead here and then returns it to
  // "sold", so this type carries the whole "sold" field set through the round
  // trip rather than only what the change order itself edits.
  "pending-change-order": {
    aia: false,
    notaxCert: null,
    intentLetter: null,
    to: null,
    quote: null,
    date: null,
    dateRequired: null,
    contracts: [],
    downPayment: null,
    deferDownPayment: false,
    deferOrdering: false,
    vendorQuotes: [],
    // The { deferOrdering, vendorQuotes } prepareForChangeOrder copied
    // from the sale. changeSale writes only the ones the change order
    // changed, so an edit made on the sale meanwhile is kept.
    changeOrderSeed: null,
    expectCustomerContract: false,
    commissionSplits: [],
  },
  sold: {
    aia: false,
    notaxCert: null,
    intentLetter: null,
    to: null,
    quote: null,
    date: null,
    dateRequired: null,
    contracts: [],
    downPayment: null,
    deferDownPayment: false,
    deferOrdering: false,
    vendorQuotes: [], // [{ vendor, number }], as on the sale
    changeOrderSeed: null, // See "pending-change-order"
    expectCustomerContract: false,
    commissionSplits: [], // Array of { userID, percent } for splitting commission with other employees
  },
  lost: {
    reason: "",
  },
};
deepFreeze(SALES_LEAD_OUTCOME_FIELDS);

export const SALES_LEAD_OUTCOMES = {
  pending: {
    order: 3,
    label: "Pending",
    icon: "pending",
    color: "positive",
  },
  "pending-sale": {
    order: 4,
    label: "Pending sale",
    icon: "pending_sale",
    color: "warning",
  },
  "pending-change-order": {
    order: 5,
    label: "Pending change order",
    icon: "change_order",
    color: "warning",
  },
  sold: {
    order: 6,
    label: "Sold",
    icon: "sold",
    color: "positive",
  },
  lost: {
    order: 7,
    label: "Lost",
    icon: "lost",
    color: "negative",
  },
};
deepFreeze(SALES_LEAD_OUTCOMES);

export const CUSTOMER_CONTRACT_TYPES = ["contract", "purchase-order"];
deepFreeze(CUSTOMER_CONTRACT_TYPES);

export default class SalesLeadOutcome extends DataStructure {
  constructor(data = {}) {
    data = SalesLeadOutcome.sanitize(data);

    super(data);

    // Documents
    if (this.data.notaxCert) {
      this.data.notaxCert = this.notaxCert.data;
    }
    if (data.intentLetter) {
      this.data.intentLetter = this.intentLetter.data;
    }
    if (data.contracts) {
      this.data.contracts = this.contracts.map((d) => d.data);
    }
  }

  // Editing

  get rules() {
    return {
      intentLetter: [
        (a) =>
          this.data.type !== "pending-sale" ||
          (this.data.contracts && this.data.contracts.length > 0) ||
          (this.intentLetter && this.intentLetter.isValid) ||
          "intentLetterOrContractsRequired",
      ],
      date: [(a) => this.data.type !== "sold" || Boolean(a) || "required"],
      dateRequired: [
        (a) => this.data.type !== "sold" || Boolean(a) || "required",
      ],
      quote: [(a) => this.data.type !== "sold" || Boolean(a) || "required"],
      contracts: [
        (a) =>
          (this.data.type !== "sold" &&
            (this.data.type !== "pending-sale" || this.data.intentLetter)) ||
          (a && this.contracts.filter((d) => d.isValid).length > 0) ||
          "required",
        (a) =>
          this.data.type !== "sold" ||
          !this.data.expectCustomerContract ||
          a.some((doc) => CUSTOMER_CONTRACT_TYPES.includes(doc.type)) ||
          "required",
      ],
      downPayment: [
        (a) =>
          this.data.type !== "sold" ||
          !this.requiresDownPayment ||
          this.data.deferDownPayment ||
          Boolean(a) ||
          "required",
      ],
      reason: [(a) => this.data.type !== "lost" || Boolean(a) || "required"],
      // The purchasers placing a deferred order need every vendor's quote.
      vendorQuotes: [
        (a) =>
          this.data.type !== "sold" ||
          !this.data.deferOrdering ||
          this.vendorsMissingQuotes(a).length === 0 ||
          "required",
      ],
    };
  }

  /**
   * The vendors on the final quote, which the sold form asks a quote number
   * of: every brand on its items but Labor.
   */
  get quoteVendors() {
    const quote = this.quote;
    if (!quote) return [];
    return uniq(
      (quote.data.items || [])
        .filter((item) => item.itemType !== "labor" && item.brand)
        .map((item) => item.brand)
    ).sort();
  }

  /** The quote's vendors that `vendorQuotes` holds no number for. */
  vendorsMissingQuotes(vendorQuotes = this.data.vendorQuotes) {
    const vendors = new Set(
      cleanVendorQuotes(vendorQuotes).map((quote) => quote.vendor)
    );
    return this.quoteVendors.filter((vendor) => !vendors.has(vendor));
  }

  get isValid() {
    return Boolean(
      every(this.rules, (rules, key) =>
        rules.every((rule) => rule(get(this.data, key)) === true)
      )
    );
  }

  get hasCustomerContract() {
    return this.data.contracts
      ? this.data.contracts.some(
          (doc) => CUSTOMER_CONTRACT_TYPES.includes(doc.type) && doc.file
        )
      : false;
  }

  static cleanDocuments(data, removeUnsaved = false) {
    if (
      data.notaxCert &&
      !new DocumentRef(data.notaxCert)[removeUnsaved ? "isSaved" : "isValid"]
    ) {
      data.notaxCert = null;
    }
    if (
      data.intentLetter &&
      !new DocumentRef(data.intentLetter)[removeUnsaved ? "isSaved" : "isValid"]
    ) {
      data.intentLetter = null;
    }
    if (data.contracts) {
      data.contracts = data.contracts.filter(
        (d) => new DocumentRef(d)[removeUnsaved ? "isSaved" : "isValid"]
      );
    }
    return data;
  }

  static sanitize(data) {
    return sanitize(data, {
      type: data.type,
      ...SALES_LEAD_OUTCOME_FIELDS[data.type],
    });
  }

  // Formatting

  get type() {
    const type = SALES_LEAD_OUTCOMES[this.data.type];
    return type ? { ...type, ...COLORS[type.color] } : null;
  }

  get notaxCert() {
    return this.data.notaxCert ? new DocumentRef(this.data.notaxCert) : null;
  }

  get intentLetter() {
    return this.data.intentLetter
      ? new DocumentRef(this.data.intentLetter)
      : null;
  }

  get contracts() {
    return this.data.contracts
      ? this.data.contracts.map((d) => new DocumentRef(d))
      : [];
  }

  get requiresDownPayment() {
    const quote = this.quote;
    return (
      quote &&
      quote.terms.cashSale &&
      quote.data.total.price * quote.data.terms.downPayment > 0
    );
  }

  get downPayment() {
    return this.data.downPayment
      ? store.state.main.transactions[this.data.downPayment]
      : null;
  }

  get downPaymentDue() {
    const quote = this.quote;
    return quote && quote.data.terms.cashSale
      ? (quote.data.total.price * quote.data.terms.downPayment) / 100
      : null;
  }

  get downPaymentDueFormatted() {
    const amount = this.downPaymentDue;
    return amount === null ? null : formatCurrency(this.downPaymentDue);
  }

  get quote() {
    return this.data.quote ? store.state.main.quotes[this.data.quote] : null;
  }

  get to() {
    return this.data.to
      ? {
          ...this.data.to,
          contact: store.state.main.contacts[this.data.to] || null,
        }
      : undefined;
  }
}
